import styled from 'styled-components';
import { type FC, useMemo, useState } from 'react';
import { Icon, IconType } from '../Props/Icon/Icon';
import { Coin } from '../Props';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import {
  type AggregatedScoreBreakdown,
  type CampaignAction,
  CampaignActionType,
  ScoreBreakdownCategories,
  type ScoreBreakdownContentEntryKey,
  useScoreBreakdownByUserQuery,
} from '../../apiHooks/useScoreBreakdownByUserQuery';
import { StatusIcon, StatusIconType } from '../Props/StatusIcon/StatusIcon';
import { NoUserDataCard } from './components/NoUserDataCard';
import { DynamicActionsSection } from './components/DynamicActionsSection';
import { Tooltip } from '../Tooltip';
import { ActionTooltip } from './components/ActionTooltip';
import { TitleContainer } from '../Card/Card';
import { Spread } from '../utility/Spread';
import { ViewActivityLogTooltip } from './components/ViewActivityLogTooltip';
import { ActivityLogModal } from './components/ActivityLogModal';
import { ScoreBreakdownTooltip } from './components/ScoreBreakdownTooltip';
import type { ActivityOverviewWidgetParams } from '../Activity/ActivityOverviewWidget';
import { GroupingType, TimeRange } from '../../apiHooks/useUserScoreTimelineQuery';
import { useMediaQuery } from '../../lib/hooks/useMediaQuery';
import { useCampaignsQuery } from '../../apiHooks/useCampaignsQuery';

type ActivityLogModalProps = {
  isOpen: boolean;
  params?: ActivityOverviewWidgetParams;
};

type TabKey = 'behaviors' | 'recentInsights';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'behaviors', label: 'Your Behaviors' },
  { key: 'recentInsights', label: 'Recent Insights' },
];

const SCORE_BREAKDOWN_CONTENT_ENTRY_MAP: Record<ScoreBreakdownCategories, ScoreBreakdownContentEntryKey> = {
  [ScoreBreakdownCategories.COMPLETION]: 'scoreBreakdownCompletion',
  [ScoreBreakdownCategories.ENGAGEMENT]: 'scoreBreakdownEngagement',
  [ScoreBreakdownCategories.EMAIL_SECURITY]: 'scoreBreakdownEmailSecurity',
  [ScoreBreakdownCategories.KNOWLEDGE]: 'scoreBreakdownKnowledge',
  [ScoreBreakdownCategories.DATA_PROTECTION]: 'scoreBreakdownDataProtection',
  [ScoreBreakdownCategories.MICROSOFT_IDENTITY]: 'scoreBreakdownMicrosoftIdentity',
  [ScoreBreakdownCategories.PHISHING_REPORTS]: 'scoreBreakdownPhishingReports',
  [ScoreBreakdownCategories.PHISHING_CLICKS]: 'scoreBreakdownPhishingClicks',
};

const SCORE_BREAKDOWN_CATEGORY_ALIASES: Record<string, ScoreBreakdownCategories> = {
  completion: ScoreBreakdownCategories.COMPLETION,
  'training & compliance': ScoreBreakdownCategories.COMPLETION,
  'training and compliance': ScoreBreakdownCategories.COMPLETION,

  engagement: ScoreBreakdownCategories.ENGAGEMENT,
  'general engagement': ScoreBreakdownCategories.ENGAGEMENT,
  'email security': ScoreBreakdownCategories.EMAIL_SECURITY,

  knowledge: ScoreBreakdownCategories.KNOWLEDGE,
  'data protection': ScoreBreakdownCategories.DATA_PROTECTION,
  'microsoft identity': ScoreBreakdownCategories.MICROSOFT_IDENTITY,

  phishingreports: ScoreBreakdownCategories.PHISHING_REPORTS,
  phishingclicks: ScoreBreakdownCategories.PHISHING_CLICKS,
  'phishing & social engineering': ScoreBreakdownCategories.PHISHING_REPORTS,
  'phishing and social engineering': ScoreBreakdownCategories.PHISHING_REPORTS,
};

const normalizeScoreBreakdownCategory = (scoreBreakdown: string) => scoreBreakdown.trim().toLowerCase();

const resolveScoreBreakdownCategory = (scoreBreakdown: string): ScoreBreakdownCategories | undefined => {
  return SCORE_BREAKDOWN_CATEGORY_ALIASES[normalizeScoreBreakdownCategory(scoreBreakdown)];
};

export const ScoreBreakdown: FC = () => {
  const scoreBreakdownByUserResponse = useScoreBreakdownByUserQuery();
  const {
    data: { mandatory: mandatoryCampaigns, voluntaryModules: voluntaryCampaigns },
  } = useCampaignsQuery(true);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeTab, setActiveTab] = useState<TabKey>('behaviors');
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [modalProps, setModalProps] = useState<ActivityLogModalProps>({
    isOpen: false,
    params: undefined,
  });

  const campaignActions: CampaignAction[] = useMemo(
    () => scoreBreakdownByUserResponse.data?.campaignActions ?? [],
    [scoreBreakdownByUserResponse.data?.campaignActions],
  );
  const noNeutralCampaignActions: CampaignAction[] = useMemo(
    () => campaignActions.filter((action) => action.result !== CampaignActionType.NEUTRAL),
    [campaignActions],
  );

  const scoreBreakdownAggregations: AggregatedScoreBreakdown[] =
    scoreBreakdownByUserResponse.data?.scoreBreakdownAggregations ?? [];

  const dynamicActionAggregations = scoreBreakdownByUserResponse.data?.dynamicActionAggregations ?? [];

  const toggleExpand = (index: number) => {
    setExpandedIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const getContentEntryKeyByScoreBreakdown = (item: AggregatedScoreBreakdown): ScoreBreakdownContentEntryKey | undefined => {
    const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);

    return mappedCategory ? SCORE_BREAKDOWN_CONTENT_ENTRY_MAP[mappedCategory] : undefined;
  };

  const getContentEntrySubKeyPrefix = (item: AggregatedScoreBreakdown): 'Positive' | 'Neutral' | 'Negative' => {
    if (item.score > 0) {
      return 'Positive';
    }
    if (item.score < 0) {
      return 'Negative';
    }

    return 'Neutral';
  };

  const handleViewFullLog = (item: AggregatedScoreBreakdown) => {
    const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);

    setModalProps({
      isOpen: true,
      params: {
        scoreBreakdowns: mappedCategory ? [mappedCategory] : [],
        groupingType: GroupingType.MONTH,
        timeRange: TimeRange.ONE_YEAR,
      },
    });
  };

  return (
    <Container>
      <TitleContainer>
        <Spread>
          <Title>
            <NamedContentEntryText container={'span'} refKey="sectionTitles" subKey="scoreBreakdown" />
            <NamedContentEntryText container={'label'} refKey="sectionTitles" subKey="last30Days" />
          </Title>
          <ViewActivityLogButton onClick={() => setModalProps({ isOpen: true, params: undefined })}>
            <NamedContentEntryText container={'span'} refKey="scoreBreakdownTooltips" subKey="viewActivityLog" />
            {!isMobile && (
              <Tooltip content={<ViewActivityLogTooltip />}>
                <Icon icon={IconType.INFO_TOOLTIP} width={16} height={16} fill="transparent" />
              </Tooltip>
            )}
          </ViewActivityLogButton>
        </Spread>
      </TitleContainer>
      <TabBar>
        {tabs.map((t) => (
          <TabButton key={t.key} $active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </TabButton>
        ))}
      </TabBar>
      {activeTab === 'behaviors' && (
        <>
          {!!scoreBreakdownAggregations.length &&
            scoreBreakdownAggregations.map((item, index) => {
              const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);
              const sentiment = getContentEntrySubKeyPrefix(item);
              const shouldShowActions =
                mappedCategory === ScoreBreakdownCategories.PHISHING_CLICKS ||
                mappedCategory === ScoreBreakdownCategories.PHISHING_REPORTS;
              const contentEntryKey = getContentEntryKeyByScoreBreakdown(item);

              return (
                <ScoreItem key={item.scoreBreakdown}>
                  {item.assetsUrl && (
                    <ScoreItemIcon>
                      <img src={item.assetsUrl} alt={item.scoreBreakdown} />
                    </ScoreItemIcon>
                  )}
                  <ScoreItemContent>
                    <ScoreItemHeader onClick={() => toggleExpand(index)}>
                      <ToggleArrow>
                        <Icon
                          icon={expandedIndices.includes(index) ? IconType.CHEVRON_DOWN : IconType.CHEVRON_RIGHT}
                          fill={'transparent'}
                        />
                      </ToggleArrow>
                      <ScoreItemHeaderTitle>
                        <span style={{ marginRight: 8 }}>{index + 1}.</span>
                        {contentEntryKey ? (
                          <NamedContentEntryText
                            refKey={contentEntryKey}
                            container={'span'}
                            subKey={`heading${sentiment}`}
                          />
                        ) : (
                          <span>{item.scoreBreakdown}</span>
                        )}
                      </ScoreItemHeaderTitle>
                    </ScoreItemHeader>
                    <ScoreItemDescription $isVisible={expandedIndices.includes(index)}>
                      {contentEntryKey && (
                        <NamedContentEntryText
                          refKey={contentEntryKey}
                          container={'span'}
                          subKey={
                            mappedCategory === ScoreBreakdownCategories.COMPLETION &&
                              item.score === 0 &&
                              !mandatoryCampaigns.length &&
                              !voluntaryCampaigns.length
                              ? (`bodyNeutralNoCampaigns` as never)
                              : `body${sentiment}`
                          }
                        />
                      )}
                      {noNeutralCampaignActions.length > 0 && shouldShowActions && (
                        <ScoreItemFooter>
                          <Icon className="rtl-flip" icon={IconType.CORNER_DOWN_RIGHT_ARROW} height={16} width={16} />
                          <ScoreItemPillsContainer>
                            <ScoreItemPillsIconsContainer>
                              {noNeutralCampaignActions.map((action) => (
                                <Tooltip
                                  content={<ActionTooltip actionResult={action.result} date={action.date} />}
                                  key={action.campaignId}
                                >
                                  <StatusIcon
                                    icon={
                                      action.result === CampaignActionType.SUCCESS
                                        ? StatusIconType.SUCCESS
                                        : action.result === CampaignActionType.NEUTRAL
                                          ? StatusIconType.NEUTRAL
                                          : StatusIconType.FAIL
                                    }
                                    height={16}
                                    width={16}
                                  />
                                </Tooltip>
                              ))}
                            </ScoreItemPillsIconsContainer>
                            <NamedContentEntryText
                              refKey={'scoreBreakdownTooltips'}
                              container={'span'}
                              subKey={'result'}
                            />
                          </ScoreItemPillsContainer>
                        </ScoreItemFooter>
                      )}
                    </ScoreItemDescription>
                  </ScoreItemContent>
                  <ScoreItemCoin>
                    <Tooltip content={<ScoreBreakdownTooltip item={item} />} onClick={() => handleViewFullLog(item)}>
                      <Coin className="score" value={item.score} />
                    </Tooltip>
                  </ScoreItemCoin>
                </ScoreItem>
              );
            })}
          {!scoreBreakdownAggregations.length && <NoUserDataCard />}
        </>
      )}
      {activeTab === 'recentInsights' &&
        (dynamicActionAggregations.length > 0 ? (
          <DynamicActionsSection items={dynamicActionAggregations} hideTitle />
        ) : (
          <NoUserDataCard />
        ))}
      <ActivityLogModal
        isOpen={modalProps.isOpen}
        onClose={() => setModalProps({ isOpen: false, params: undefined })}
        params={modalProps.params}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabBar = styled.div`
  display: inline-flex;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  padding: 3px;
  background: ${({ theme }) => theme.colors.prototype.secondary.replace(')', ' / 50%)')};
  width: fit-content;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    box-shadow 0.15s;
  background: ${({ $active, theme }) => ($active ? theme.colors.prototype.card : 'transparent')};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.prototype.foreground : theme.colors.prototype.mutedForeground};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};

  &:hover {
    color: ${({ theme }) => theme.colors.prototype.foreground};
  }
`;

const ScoreItem = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 12px;
  padding: 10px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const ScoreItemIcon = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  max-width: 48px;
  max-height: 48px;
  background: ${({ theme }) => theme.colors.prototype.secondaryForeground};
  border-radius: 50%;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;

    img {
      width: 32px;
      height: 32px;
    }
  }
`;

const ScoreItemContent = styled.div`
  width: 100%;
  margin: 0 20px;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const ScoreItemHeader = styled.div`
  display: flex;
`;

const ScoreItemHeaderTitle = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  font-weight: 600;
  letter-spacing: 0.28px;
  margin-right: 20px;

  @media (max-width: 768px) {
    margin-right: 10px;
  }
`;

const ScoreItemCoin = styled.div`
  display: flex;
  align-items: start;

  .score {
    cursor: pointer;
  }
`;

const ToggleArrow = styled.div`
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-right: 16px;
  }
`;

const ScoreItemDescription = styled.div<{ $isVisible: boolean }>`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  @media (max-width: 768px) {
    display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  }
`;

const ScoreItemFooter = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
`;

const ScoreItemPillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  background: ${({ theme }) => theme.colors.prototype.secondary};
  border-radius: 16px;
  padding: 4px 8px 4px 4px;
  gap: 6px;
  align-items: center;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  font-size: 12px;
  font-weight: 500;
  /* This margin is to align the pills with the icon */
  margin-top: 4px;
`;

const ScoreItemPillsIconsContainer = styled.div`
  display: flex;
  gap: 2px;
`;

const ViewActivityLogButton = styled.button`
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.prototype.primary};
  text-decoration: underline;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  line-height: 32px;

  > label {
    text-transform: none;
    color: ${({ theme }) => theme.colors.prototype.foreground};
    font-size: 12px;
    font-weight: 400;
  }
`;
